import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { paymentService, type PaymentMethodInfo } from '@/services/payment.service';
import toast from 'react-hot-toast';

interface AddCardFormProps {
  onSuccess: (method: PaymentMethodInfo) => void;
  onCancel: () => void;
}

// Inner form that uses Stripe hooks (must be inside Elements)
const CardForm: React.FC<{
  clientSecret: string;
  onSuccess: (method: PaymentMethodInfo) => void;
  onCancel: () => void;
}> = ({ clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingName, setBillingName] = useState('');
  const [billingLine1, setBillingLine1] = useState('');
  const [billingLine2, setBillingLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostal, setBillingPostal] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingName || undefined,
            address: {
              line1: billingLine1 || undefined,
              line2: billingLine2 || undefined,
              city: billingCity || undefined,
              state: billingState || undefined,
              postal_code: billingPostal || undefined,
              country: billingCountry || undefined,
            },
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to confirm card');
        setLoading(false);
        return;
      }

      if (setupIntent?.payment_method) {
        const pmId = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

        const saved = await paymentService.confirmMethod(pmId);
        toast.success('Card added successfully');
        onSuccess(saved);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Billing Information */}
      <div className="space-y-3">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Billing Information</p>
        <div>
          <input
            type="text"
            value={billingName}
            onChange={(e) => setBillingName(e.target.value)}
            placeholder="Name on card"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
        </div>
        <div>
          <input
            type="text"
            value={billingLine1}
            onChange={(e) => setBillingLine1(e.target.value)}
            placeholder="Address line 1"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
        </div>
        <div>
          <input
            type="text"
            value={billingLine2}
            onChange={(e) => setBillingLine2(e.target.value)}
            placeholder="Address line 2 (optional)"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={billingCity}
            onChange={(e) => setBillingCity(e.target.value)}
            placeholder="City"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
          <input
            type="text"
            value={billingState}
            onChange={(e) => setBillingState(e.target.value)}
            placeholder="State / Province"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={billingPostal}
            onChange={(e) => setBillingPostal(e.target.value)}
            placeholder="ZIP / Postal code"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
          <input
            type="text"
            value={billingCountry}
            onChange={(e) => setBillingCountry(e.target.value)}
            placeholder="Country (e.g. US)"
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
          />
        </div>
      </div>

      {/* Card Details */}
      <div className="space-y-3">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Card Details</p>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1a1a2e',
                  '::placeholder': { color: '#9ca3af' },
                },
                invalid: { color: '#ef4444' },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] text-white rounded-lg hover:bg-[#2d2d4e] disabled:opacity-50 transition-colors text-sm font-medium"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
          {loading ? 'Saving...' : 'Save Card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Wrapper that loads Stripe and creates SetupIntent
export const AddCardForm: React.FC<AddCardFormProps> = ({ onSuccess, onCancel }) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // Fetch Stripe publishable key
        const config = await paymentService.getConfig();
        if (cancelled) return;

        if (!config.publishable_key) {
          setError('Payment not configured. Contact support.');
          setLoading(false);
          return;
        }

        setStripePromise(loadStripe(config.publishable_key));

        // Create SetupIntent
        const intent = await paymentService.createSetupIntent();
        if (cancelled) return;

        setClientSecret(intent.client_secret);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to initialize payment form');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading payment form...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-4 rounded-lg">
        <AlertCircle className="w-4 h-4 shrink-0" />
        {error}
        <button onClick={onCancel} className="ml-auto text-gray-500 hover:text-gray-700 text-sm">
          Close
        </button>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) return null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
        <CreditCard className="w-4 h-4" />
        Add Payment Method
      </div>
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <CardForm clientSecret={clientSecret} onSuccess={onSuccess} onCancel={onCancel} />
      </Elements>
    </div>
  );
};
